import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import AdminGuard from "./components/auth/AdminGuard";

const ConstructionDustMonitoring = lazy(() => import("./pages/ConstructionDustMonitoring"));
const DustMonitoringServices = lazy(() => import("./pages/DustMonitoringServices"));
const RealTimeDustMonitoring = lazy(() => import("./pages/RealTimeDustMonitoring"));
const DustMonitoringPlanningConditions = lazy(() => import("./pages/DustMonitoringPlanningConditions"));
const PM10Monitoring = lazy(() => import("./pages/PM10Monitoring"));
const PM25Monitoring = lazy(() => import("./pages/PM25Monitoring"));
const DemolitionDustMonitoring = lazy(() => import("./pages/DemolitionDustMonitoring"));
const DustComplaintInvestigation = lazy(() => import("./pages/DustComplaintInvestigation"));
const Contact = lazy(() => import("./pages/Contact"));
const RequestQuote = lazy(() => import("./pages/RequestQuote"));
const FAQ = lazy(() => import("./pages/FAQ"));
const BoroughPage = lazy(() => import("./pages/BoroughPage"));
const SeoDashboard = lazy(() => import("./pages/SeoDashboard"));
const SeoSummary = lazy(() => import("./pages/SeoSummary"));
const SeoDashboardSettings = lazy(() => import("./pages/SeoDashboardSettings"));
const SeoDashboardConnectionGuide = lazy(() => import("./pages/SeoDashboardConnectionGuide"));
const Auth = lazy(() => import("./pages/Auth"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/construction-dust-monitoring-london" element={<ConstructionDustMonitoring />} />
              <Route path="/dust-monitoring-services-london" element={<DustMonitoringServices />} />
              <Route path="/real-time-dust-monitoring" element={<RealTimeDustMonitoring />} />
              <Route path="/dust-monitoring-planning-conditions" element={<DustMonitoringPlanningConditions />} />
              <Route path="/pm10-monitoring-construction" element={<PM10Monitoring />} />
              <Route path="/pm25-monitoring-construction" element={<PM25Monitoring />} />
              <Route path="/dust-monitoring-demolition-sites" element={<DemolitionDustMonitoring />} />
              <Route path="/dust-monitoring-complaints" element={<DustComplaintInvestigation />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/request-quote" element={<RequestQuote />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/dust-monitoring/:slug" element={<BoroughPage />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/seo-dashboard" element={<AdminGuard><SeoDashboard /></AdminGuard>} />
              <Route path="/seo-summary" element={<AdminGuard><SeoSummary /></AdminGuard>} />
              <Route path="/seo-dashboard-settings" element={<AdminGuard><SeoDashboardSettings /></AdminGuard>} />
              <Route path="/seo-dashboard-connection-guide" element={<AdminGuard><SeoDashboardConnectionGuide /></AdminGuard>} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
